/**
 * Environment Diagnostics (P20250925-117)
 *
 * Safe, categorized view of environment configuration for admin operators.
 * Redacts secrets; shows presence/values of non-sensitive config only.
 */

import { maskValue } from './redact';
import {
  getLaunchFlags,
  getIntegrationFlags,
  getEscrowEnv,
  getClarityId,
  getSalesIQId,
} from '@/lib/config/flags';

export interface EnvCategory {
  name: string;
  items: EnvItem[];
}

export interface EnvItem {
  key: string;
  value: string;
  sensitive: boolean;
  description?: string;
}

/**
 * Get Core (Launch & Operations) flags
 */
function getCoreEnv(): EnvCategory {
  const flags = getLaunchFlags();

  return {
    name: 'Core (Launch & Operations)',
    items: [
      {
        key: 'VITE_LAUNCH_MODE',
        value: flags.LAUNCH_MODE ? 'true' : 'false',
        sensitive: false,
        description: 'Controls search engine indexing and live status',
      },
      {
        key: 'VITE_MAINTENANCE_MODE',
        value: flags.MAINTENANCE_MODE ? 'true' : 'false',
        sensitive: false,
        description: 'Shows maintenance page to non-admin users',
      },
      {
        key: 'VITE_CHECKOUT_DISABLED',
        value: flags.CHECKOUT_DISABLED ? 'true' : 'false',
        sensitive: false,
        description: 'Disables purchase CTAs',
      },
      {
        key: 'VITE_SUPABASE_URL',
        value: import.meta.env.VITE_SUPABASE_URL || '(not set)',
        sensitive: false,
        description: 'Supabase project URL',
      },
      {
        key: 'VITE_SUPABASE_PUBLISHABLE_KEY',
        value: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
          ? maskValue(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY)
          : '(not set)',
        sensitive: true,
        description: 'Supabase anon key (masked)',
      },
    ],
  };
}

/**
 * Get our trusted payment provider integration env
 */
function getEscrowEnvVars(): EnvCategory {
  return {
    name: 'our trusted payment provider',
    items: [
      {
        key: 'VITE_ESCROW_ENV',
        value: getEscrowEnv(),
        sensitive: false,
        description: 'Transaction environment (sandbox or live)',
      },
      {
        key: 'ESCROW_API_KEY',
        value: '(server-side secret)',
        sensitive: true,
        description: 'API key presence checked server-side',
      },
    ],
  };
}

/**
 * Get Analytics integration env
 */
function getAnalyticsEnv(): EnvCategory {
  const flags = getIntegrationFlags();

  return {
    name: 'Analytics & Monitoring',
    items: [
      {
        key: 'VITE_POSTHOG_KEY',
        value: import.meta.env.VITE_POSTHOG_KEY
          ? maskValue(import.meta.env.VITE_POSTHOG_KEY)
          : '(not set)',
        sensitive: true,
        description: 'PostHog project key (masked)',
      },
      {
        key: 'VITE_POSTHOG_HOST',
        value: import.meta.env.VITE_POSTHOG_HOST || '(not set)',
        sensitive: false,
        description: 'PostHog API host',
      },
      {
        key: 'VITE_CLARITY_ENABLED',
        value: flags.clarity.enabled ? 'true' : 'false',
        sensitive: false,
        description: 'Microsoft Clarity session recordings',
      },
      {
        key: 'VITE_CLARITY_ID',
        value: getClarityId() ? maskValue(getClarityId()!) : '(not set)',
        sensitive: true,
        description: 'Clarity project ID (masked)',
      },
    ],
  };
}

/**
 * Get Zoho integration env
 */
function getZohoEnv(): EnvCategory {
  const flags = getIntegrationFlags();

  return {
    name: 'Zoho Integrations',
    items: [
      {
        key: 'VITE_SALESIQ_ENABLED',
        value: flags.salesiq.enabled ? 'true' : 'false',
        sensitive: false,
        description: 'Live chat widget',
      },
      {
        key: 'VITE_SALESIQ_WIDGET_ID',
        value: getSalesIQId() ? maskValue(getSalesIQId()!) : '(not set)',
        sensitive: true,
        description: 'SalesIQ widget ID (masked)',
      },
      {
        key: 'VITE_ZOHO_CRM_ENABLED',
        value: flags.zohoCRM.enabled ? 'true' : 'false',
        sensitive: false,
        description: 'Lead capture (server-side)',
      },
      {
        key: 'VITE_ZOHO_DESK_ENABLED',
        value: flags.zohoDesk.enabled ? 'true' : 'false',
        sensitive: false,
        description: 'Ticket creation (server-side)',
      },
      {
        key: 'VITE_ZOHO_INVOICE_PORTAL_URL',
        value: import.meta.env.VITE_ZOHO_INVOICE_PORTAL_URL || '(not set)',
        sensitive: false,
        description: 'Invoice portal link',
      },
      {
        key: 'VITE_ZOHO_BOOKINGS_URL',
        value: import.meta.env.VITE_ZOHO_BOOKINGS_URL || '(not set)',
        sensitive: false,
        description: 'Consultation booking link',
      },
    ],
  };
}

/**
 * Get Experiments configuration
 */
function getExperimentsEnv(): EnvCategory {
  return {
    name: 'Experiments & Feature Flags',
    items: [
      {
        key: 'Experiment Framework',
        value: 'PostHog-backed A/B tests',
        sensitive: false,
        description: 'Current: EXP_BIN_CTA_V1',
      },
      {
        key: 'Flag Overrides',
        value: 'URL params + localStorage',
        sensitive: false,
        description: 'QA can force variants via ?qa_exp=...',
      },
    ],
  };
}

/**
 * Get all environment categories for admin display
 */
export function getAllEnvCategories(): EnvCategory[] {
  return [getCoreEnv(), getEscrowEnvVars(), getAnalyticsEnv(), getZohoEnv(), getExperimentsEnv()];
}

/**
 * Get escrow environment display value for badge
 */
export function getEscrowEnvDisplay(): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive';
} {
  const env = getEscrowEnv();
  return {
    label: env.toUpperCase(),
    variant: env === 'live' ? 'destructive' : 'secondary',
  };
}
